import React, { useState } from 'react';
import JWCarousel from '../../brand/JWCarousel';
import MiniJWCarousel from '../../brand/JWCarousel/MiniJWCarousel';
import EditHighlightsModal from '../../modals/EditHighlightsModal';
import UploadHighlightsModal from '../../modals/UploadHighlightsModal';
import ViewHighlightsModal from '../../modals/ViewHighlightsModalLazy';
import { AddButtonIcon, PencilEditIcon } from '../../icons';
import { JWCarouselSlideProps } from '../../brand/JWCarousel/JWCarouselSlide';
import { MiniJWCarouselSlideProps } from '../../brand/JWCarousel/MiniJWCarousel';
import { ImageType, ProfileInfoType, HighlightType } from '../../../types/profile';
import '../../brand/JWCarousel/styles/minihighlights.css';
import PlusIcon from '../../icons/PlusIcon';
import { PlaceholderLabel } from '../PlaceholderLabel';
import SpotifyIcon from '../../icons/SpotifyIcon';
import SoundcloudIconSVG from '../../icons/SoundcloudIconSVG';
import YoutubeIcon from '../../icons/YoutubeIcon';
import { JamBoardPin } from '../../../types/jamboard';
import {
    getModalCurrentIndex,
    getModalNavigableHighlights,
} from '../../../utils/highlightModalNavigation';
import { findHighlightIndex } from '../../../utils/highlightIdentity';

const JAMBOARD_PIN_COLOR_HEX: Record<string, string> = {
    red: '#dc2626',
    orange: '#ea580c',
    yellow: '#ca8a04',
    lime: '#65a30d',
    green: '#16a34a',
    teal: '#0d9488',
    cyan: '#0891b2',
    blue: '#2563eb',
    indigo: '#4f46e5',
    violet: '#7c3aed',
    magenta: '#db2777',
    black: '#111827',
};
const JAMBOARD_FALLBACK_PIN_COLOR = JAMBOARD_PIN_COLOR_HEX.red;

function fallbackColorKeyForPinId(pinId: string): string {
    let hash = 0;
    for (let i = 0; i < pinId.length; i += 1) {
        hash = (Math.imul(31, hash) + pinId.charCodeAt(i)) >>> 0;
    }
    const keys = Object.keys(JAMBOARD_PIN_COLOR_HEX);
    return keys[hash % keys.length];
}

function resolveJamBoardPinColor(
    colorKeyOrHex: string | null | undefined,
    pinId: string
): string {
    if (!colorKeyOrHex) {
        return (
            JAMBOARD_PIN_COLOR_HEX[fallbackColorKeyForPinId(pinId)] ||
            JAMBOARD_FALLBACK_PIN_COLOR
        );
    }
    const normalized = colorKeyOrHex.trim().toLowerCase();
    if (normalized.startsWith('#')) {
        return normalized;
    }
    return (
        JAMBOARD_PIN_COLOR_HEX[normalized] ||
        JAMBOARD_PIN_COLOR_HEX[fallbackColorKeyForPinId(pinId)] ||
        JAMBOARD_FALLBACK_PIN_COLOR
    );
}

function currencySymbolForCode(code: string): string | null {
    const normalized = code.toUpperCase();
    if (normalized === 'USD') return '$';
    if (normalized === 'EUR') return '€';
    if (normalized === 'GBP') return '£';
    if (normalized === 'CAD') return 'C$';
    if (normalized === 'AUD') return 'A$';
    if (normalized === 'NZD') return 'NZ$';
    if (normalized === 'JPY') return '¥';
    return null;
}

function formatJamBoardRateLabel(
    paymentText: string | null | undefined,
    paymentCurrency: string | null | undefined
): string | undefined {
    const rawText = paymentText?.trim();
    if (!rawText) return 'FREE';
    const numericMatch = rawText.match(/\d+(?:[.,]\d+)?/);
    if (!numericMatch) {
        return 'FREE';
    }
    const numericValue = Number(numericMatch[0].replace(/,/g, ''));
    if (!Number.isFinite(numericValue)) {
        return 'FREE';
    }
    if (numericValue <= 0) {
        return 'FREE';
    }
    const formattedNumber = Number.isInteger(numericValue)
        ? numericValue.toLocaleString('en-US')
        : numericValue.toLocaleString('en-US', {
            maximumFractionDigits: 2,
        });
    const inlineCurrencyMatch = rawText.match(
        /\b(USD|EUR|GBP|CAD|AUD|NZD|JPY)\b/i
    );
    const resolvedCurrency =
        paymentCurrency || inlineCurrencyMatch?.[1] || 'USD';
    const symbol = currencySymbolForCode(resolvedCurrency) || '$';
    return symbol ? `${symbol}${formattedNumber}` : formattedNumber;
}

interface HighlightsProps {
    id: string
    profileInfo?: ProfileInfoType | null
    setProfileInfo: (profileInfo: ProfileInfoType) => void
    isLoading: boolean
    onProfileSave: (nextProfile?: ProfileInfoType) => void
    canEdit: boolean
    mobile?: boolean
    activeJamBoardPins?: JamBoardPin[]
}

function Highlights({
    id,
    profileInfo,
    setProfileInfo,
    isLoading,
    onProfileSave,
    canEdit = false,
    mobile = false,
    activeJamBoardPins = [],
}: HighlightsProps) {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedViewItem, setSelectedViewItem] =
        useState<HighlightType | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);

    const sortedActiveJamBoardPins = React.useMemo(
        () =>
            [...activeJamBoardPins].sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            ),
        [activeJamBoardPins]
    );

    const jamBoardPinsDisplayMode =
        profileInfo?.jamBoardPinsDisplayMode || 'most_recent';

    const hiddenJamBoardPinIds = React.useMemo(
        () => new Set((profileInfo?.hiddenJamBoardPinIds || []).map((id) => String(id))),
        [profileInfo?.hiddenJamBoardPinIds]
    );

    const jamBoardPinsToDisplay = React.useMemo(() => {
        if (jamBoardPinsDisplayMode === 'none') {
            return [];
        }
        if (!sortedActiveJamBoardPins.length) {
            return [];
        }
        if (jamBoardPinsDisplayMode === 'all') {
            return sortedActiveJamBoardPins.filter(
                (pin) => !hiddenJamBoardPinIds.has(String(pin._id))
            );
        }
        return [sortedActiveJamBoardPins[0]];
    }, [jamBoardPinsDisplayMode, sortedActiveJamBoardPins, hiddenJamBoardPinIds]);

    const jamBoardVirtualHighlights = React.useMemo(() => (
        jamBoardPinsToDisplay.map((pin) => {
            const locationText =
                pin.locationMode === 'remote'
                    ? 'Remote'
                    : pin.location?.displayText || 'Location on profile';
            return {
                url: '',
                image: undefined,
                imagePath: '',
                description: pin.title,
                mediaUrl: '',
                coverArtUrl: pin.projectImage || '',
                typeCd: 'JB',
                canEdit: false,
                isVirtualJamBoardHighlight: true,
                jamBoardPinId: String(pin._id),
                jamBoardLocationText: locationText,
                jamBoardPinColor: resolveJamBoardPinColor(
                    pin.pinColor,
                    pin._id
                ),
                jamBoardRateLabel: formatJamBoardRateLabel(
                    pin.paymentText,
                    pin.paymentCurrency
                ),
            } as HighlightType;
        })
    ), [jamBoardPinsToDisplay]);

    const displayHighlights = React.useMemo(() => {
        const realHighlights = profileInfo?.highlights || [];
        if (!jamBoardVirtualHighlights.length) {
            return realHighlights;
        }
        return [...jamBoardVirtualHighlights, ...realHighlights];
    }, [jamBoardVirtualHighlights, profileInfo?.highlights]);

    const modalNavigableHighlights = React.useMemo(
        () => getModalNavigableHighlights(displayHighlights),
        [displayHighlights]
    );

    const saveHighlights = (
        highlightsToSave: HighlightType[],
        profileOverrides: Partial<ProfileInfoType> = {}
    ) => {
        if (!profileInfo?._id) return;

        // Enforce the maximum limit of 30 highlights
        let nextHighlights = highlightsToSave;
        if (nextHighlights.length > 30) {
            nextHighlights = nextHighlights.slice(0, 30);
            alert('Maximum limit of 30 highlights, remove some to add more.');
        }

        const nextProfile = {
            ...profileInfo,
            ...profileOverrides,
            highlights: nextHighlights,
        };
        setProfileInfo(nextProfile);
        onProfileSave(nextProfile);
    };

    const addHighlight = async (
        caption: string,
        url: string,
        image: ImageType | undefined,
        mediaUrl?: string,
        coverArtUrl?: string,
        typeCd?: string
    ) => {
        const modifiedHighlights = [...(profileInfo?.highlights || [])];

        // Check if we've reached the maximum limit of 30 highlights
        if (modifiedHighlights.length >= 30) {
            alert('Maximum limit of 30 highlights reached. Please delete some highlights before adding more.');
            setShowUploadModal(false);
            return;
        }

        modifiedHighlights.unshift({
            url: url,
            image: image,
            imagePath: '',
            description: caption,
            mediaUrl: mediaUrl,
            coverArtUrl: coverArtUrl,
            typeCd: typeCd,
        });
        const modifiedProfileInfo = profileInfo
            ? { ...profileInfo, highlights: modifiedHighlights }
            : null;
        if (modifiedProfileInfo) {
            await saveHighlights(modifiedHighlights);
        }
        setShowUploadModal(false);
    };

    const deleteHighlight = (index: number) => {
        const modifiedHighlights = [...(profileInfo?.highlights || [])];
        modifiedHighlights.splice(index, 1);
        const modifiedProfileInfo = profileInfo
            ? { ...profileInfo, highlights: modifiedHighlights }
            : null;
        if (modifiedProfileInfo) {
            saveHighlights(modifiedHighlights);
        }
    };

    const editHighlightsClick = () => {
        setShowEditModal(true);
        setShowUploadModal(false);
    };

    const addHighlightClick = () => {
        setShowUploadModal(true);
        setShowEditModal(false);
    };

    const onSlideClick = (item: JWCarouselSlideProps, index: number) => {
        setSelectedViewItem(item as HighlightType);
        setSelectedIndex(index);
        setShowViewModal(true);
    };

    const handleNavigate = (direction: 'prev' | 'next') => {
        if (!modalNavigableHighlights || modalNavigableHighlights.length === 0) return;

        const currentIndex = getModalCurrentIndex(
            modalNavigableHighlights,
            selectedViewItem
        );

        const newIndex = direction === 'prev'
            ? (currentIndex - 1 + modalNavigableHighlights.length) % modalNavigableHighlights.length
            : (currentIndex + 1) % modalNavigableHighlights.length;

        setSelectedIndex(newIndex);
        setSelectedViewItem(modalNavigableHighlights[newIndex]);
    };

    const canNavigatePrev = Boolean(modalNavigableHighlights && modalNavigableHighlights.length > 1);
    const canNavigateNext = Boolean(modalNavigableHighlights && modalNavigableHighlights.length > 1);

    const navigateToAddHighlightModal = () => {
        setShowEditModal(false);
        setShowUploadModal(true);
    };

    const handleSlideClick = (item: MiniJWCarouselSlideProps, index: number) => {
        setSelectedViewItem(item as HighlightType);
        setSelectedIndex(index);
        setShowViewModal(true);
    };

    return (
        <div className="w-full h-full relative">
            {mobile ? (
                <>
                    {(!profileInfo?.highlights || profileInfo.highlights.length === 0) && canEdit ? (
                        <div className="mini-carousel-container">
                            {/* Placeholder for empty highlights in mobile view */}
                            <div className="no-slides text-purple w-full h-full">
                                <button
                                    onClick={addHighlightClick}
                                    className="group flex-col items-center justify-center space-y-3 w-full h-full bg-blue-200 rounded-lg pt-2 pb-2 pl-2 flex-grow transition-colors hover:bg-blue-300"
                                    aria-label="Add a highlight. Let them hear you"
                                    style={{
                                        paddingRight: '2px',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        minHeight: '150px',
                                        maxHeight: 'none'
                                    }}
                                >
                                    <div className="flex flex-col items-center justify-center">
                                        <PlusIcon />
                                        <p><PlaceholderLabel title="Add a highlight" benefit="Let them hear you" /></p>
                                    </div>
                                    <div className="flex items-center justify-center mt-3 space-x-3">
                                        <SpotifyIcon />
                                        <span className="text-gray-400">|</span>
                                        <SoundcloudIconSVG />
                                        <span className="text-gray-400">|</span>
                                        <YoutubeIcon />
                                    </div>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="profile-mini-carousel-wrapper">
                            <MiniJWCarousel
                                id={id}
                                slides={displayHighlights as unknown as MiniJWCarouselSlideProps[] | null}
                                onSlideClick={(item, index) => handleSlideClick(item, index)}
                                isLoading={isLoading}
                                canEdit={canEdit}
                                slidesPerView={1}
                                slidesPerGroup={1}
                                addHighlightsClick={addHighlightClick}
                            />
                        </div>
                    )}
                </>
            ) : (
                <JWCarousel
                    id={id}
                    slides={displayHighlights}
                    onSlideClick={(item, index) => onSlideClick(item, index)}
                    isLoading={isLoading}
                    canEdit={canEdit}
                    slidesPerView={mobile ? 1 : 3.5}
                    slidesPerGroup={mobile ? 1 : 2}
                    addHighlightsClick={addHighlightClick}
                />
            )}
            <EditHighlightsModal
                highlights={profileInfo?.highlights}
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                deleteHighlight={deleteHighlight}
                saveHighlights={saveHighlights}
                profileInfo={profileInfo}
                setProfileInfo={setProfileInfo}
                navigateToAddHighlightModal={navigateToAddHighlightModal}
                activeJamBoardPins={activeJamBoardPins}
                jamBoardPinsDisplayMode={profileInfo?.jamBoardPinsDisplayMode || 'most_recent'}
                hiddenJamBoardPinIds={profileInfo?.hiddenJamBoardPinIds || []}
            />
            <UploadHighlightsModal
                show={showUploadModal}
                setShow={setShowUploadModal}
                editHighlightsClick={editHighlightsClick}
                onClose={() => setShowUploadModal(false)}
                addHighlight={addHighlight}
                highlights={profileInfo?.highlights}
            />
            <ViewHighlightsModal
                show={showViewModal}
                setShow={setShowViewModal}
                highlight={selectedViewItem}
                onClose={() => setShowViewModal(false)}
                onNavigate={handleNavigate}
                canNavigatePrev={canNavigatePrev}
                canNavigateNext={canNavigateNext}
                isOwnHighlight={
                    canEdit && !selectedViewItem?.isVirtualJamBoardHighlight
                }
                activeJamBoardPins={activeJamBoardPins}
                jamBoardPinsDisplayMode={profileInfo?.jamBoardPinsDisplayMode || 'most_recent'}
                hiddenJamBoardPinIds={profileInfo?.hiddenJamBoardPinIds || []}
                canApplyToJamBoardPins={!canEdit}
                onDeleteHighlight={() => {
                    const highlightIndex = findHighlightIndex(
                        profileInfo?.highlights,
                        selectedViewItem
                    );
                    if (highlightIndex !== -1) {
                        deleteHighlight(highlightIndex);
                        setShowViewModal(false);
                    }
                }}
            />
            {canEdit &&
                profileInfo?.highlights && (
                <div>
                    <button
                        onClick={editHighlightsClick}
                        className="absolute z-20 bottom-1 right-0 bg-white p-[6px] rounded-full border-gray-400 border-[1px] cursor-pointer"
                    >
                        <PencilEditIcon
                            width={24}
                            height={24}
                            fill={'#626262'}
                            fillOpacity={0.8}
                        />
                    </button>
                    {/* Only show add button if we're below the 30 highlights limit */}
                    {(!profileInfo?.highlights || profileInfo.highlights.length < 30) && (
                        <button
                            onClick={addHighlightClick}
                            className="absolute z-20 bottom-1 right-[45px] bg-white p-[6px] rounded-full border-gray-400 border-[1px] cursor-pointer"
                        >
                            <AddButtonIcon
                                width={24}
                                height={24}
                                fill={'#626262'}
                                fillOpacity={0.8}
                            />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default Highlights;
